import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Heading,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { AccountType, DataType, ProviderDataMap, ProviderDataMapType, SurchargeCategory } from '@pelicargo/types';
import { useError, useSuccess } from '@pelicargo/ui';
import { Show } from '@pelicargo/ui';
import { FreightService, IdentitySystem } from '@prisma/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { trpc } from '../../config/trpc';
import { BaseDrawer } from '../BaseDrawer';
import { ProviderApiForm } from '../Forms/ProviderApiForm';
import { ProviderForm } from '../Forms/ProviderForm';
import { ProviderIdentityForm } from '../Forms/ProviderIdentityForm';
import { SimpleForm } from '../SimpleForm';

const schema = z.object({
  logo_uri: z.string().nullable(),
  name: z.string().min(1, 'Required'),
  id: z.string().min(1, 'Required'),
  awb_prefix: z.string().nullable(),
  airports: z.array(z.string()).min(1, 'Required'),
  supported_account_types: z.array(z.nativeEnum(AccountType)),
  required_surcharge_categories: z.array(z.nativeEnum(SurchargeCategory)),
  is_active: z.boolean(),
  provider_accounts: z.array(z.coerce.number()).nullable(),
  has_api_booking: z.boolean(),
  has_api_cancellation: z.boolean(),
  has_api_quoting: z.boolean(),
  has_api_booking_without_awb: z.boolean(),
  terms_conditions_url: z.string().nullish(),
  allows_pelicargo_settled_payments: z.boolean(),
  provided_services: z.array(z.nativeEnum(FreightService)),
  direct_tracking_url: z.string().url().nullish(),

  mapped_data_by_type: z.object({
    [ProviderDataMapType.API_SURCHARGE_NAME_TO_PELI_SURCHARGE_TYPE]: z
      .array(
        z.object({
          id: z.coerce.number().int().default(-1),
          type: z
            .nativeEnum(ProviderDataMapType)
            .default(ProviderDataMapType.API_SURCHARGE_NAME_TO_PELI_SURCHARGE_TYPE),
          checkValue: z.string(),
          checkValueType: z.nativeEnum(DataType),
          resultValue: z.string(),
          resultValueType: z.nativeEnum(DataType),
        }),
      )
      .default([]),
    [ProviderDataMapType.API_FLIGHT_SEGMENT_STATUS_TO_PELI_FLIGHT_SEGMENT_STATUS]: z
      .array(
        z.object({
          id: z.coerce.number().int().default(-1),
          type: z
            .nativeEnum(ProviderDataMapType)
            .default(ProviderDataMapType.API_FLIGHT_SEGMENT_STATUS_TO_PELI_FLIGHT_SEGMENT_STATUS),
          checkValue: z.string(),
          checkValueType: z.nativeEnum(DataType),
          resultValue: z.string(),
          resultValueType: z.nativeEnum(DataType),
        }),
      )
      .default([]),
  }),

  provider_identities: z.array(
    z.object({
      id: z.coerce.number().int().default(-1),
      system: z.nativeEnum(IdentitySystem),
      value: z.string(),
    }),
  ),
});

type FormValues = z.infer<typeof schema>;

type Props = {
  providerId?: string | null;
  isOpen: boolean;
  onClose: () => void;
};

const reduceMappedDataToTypedGroup = (acc, cur) => {
  if (!cur?.type) return acc;

  acc[cur?.type] ??= [];
  acc[cur?.type].push(cur);

  return acc;
};

const defaultValues = {
  logo_uri: '',
  name: '',
  id: '',
  awb_prefix: '',
  airports: [],
  supported_account_types: [],
  required_surcharge_categories: [],
  is_active: false,
  provider_accounts: [],
  has_api_booking: false,
  has_api_cancellation: false,
  has_api_quoting: false,
  has_api_booking_without_awb: false,
  terms_conditions_url: '',
  allows_pelicargo_settled_payments: false,
  direct_tracking_url: '',
  mapped_data_by_type: Object.values(ProviderDataMapType).reduce(
    reduceMappedDataToTypedGroup,
    {} as Record<ProviderDataMapType, ProviderDataMap[]>,
  ),
  provider_identities: [],
  provided_services: [],
};

export const ProviderFormDrawer = ({ providerId, isOpen, onClose }: Props) => {
  const { handleSuccess } = useSuccess();
  const { handleError } = useError();
  const methods = useForm({ defaultValues, resolver: zodResolver(schema) });
  const { reset } = methods;

  const { data, refetch } = trpc.getProvider.useQuery({ id: providerId as string }, { enabled: !!providerId });
  const { mutateAsync: createProvider, isLoading: isCreating } = trpc.createProvider.useMutation();
  const { mutateAsync: updateProvider, isLoading: isUpdating } = trpc.updateProvider.useMutation();

  const isLoading = isCreating || isUpdating;

  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (!providerId) reset(defaultValues);

    if (data?.id) {
      reset({
        logo_uri: data?.logo_uri ?? '',
        name: data?.name ?? '',
        id: data?.id ?? '',
        awb_prefix: data?.awb_prefix ?? '',
        airports: data?.airports?.map((airport) => airport?.id) ?? [],
        supported_account_types: data?.supported_account_types ?? [],
        required_surcharge_categories: data?.required_surcharge_categories ?? [],
        is_active: data?.is_active ?? false,
        provider_accounts: data?.provider_accounts?.map((account) => account?.account_id) ?? [],
        has_api_booking: data?.has_api_booking ?? false,
        has_api_cancellation: data?.has_api_cancellation ?? false,
        has_api_quoting: data?.has_api_quoting ?? false,
        has_api_booking_without_awb: data?.has_api_booking_without_awb ?? false,
        terms_conditions_url: data?.terms_conditions_url ?? '',

        allows_pelicargo_settled_payments: data?.allows_pelicargo_settled_payments ?? false,
        direct_tracking_url: data?.direct_tracking_url ?? '',
        mapped_data_by_type:
          data?.mapped_data?.reduce(
            reduceMappedDataToTypedGroup,
            {} as Record<ProviderDataMapType, ProviderDataMap[]>,
          ) || {},
        provider_identities: data?.provider_identities ?? [],
        provided_services: data?.provided_services ?? [],
      });
    }
  }, [data, providerId, reset]);

  const copy = useMemo(() => {
    return providerId ? `Update ${providerId}` : 'Create Provider';
  }, [providerId]);

  const onValid = useCallback(
    async (values: FormValues) => {
      try {
        if (providerId) await updateProvider(values);
        if (!providerId) await createProvider(values);
        await refetch();

        onClose();
        handleSuccess(providerId ? 'Provider updated' : 'Provider created');
      } catch (error) {
        handleError(error);
      }
    },
    [providerId, updateProvider, createProvider, refetch, onClose, handleSuccess, handleError],
  );

  const handleSubmit = methods.handleSubmit(onValid, console.error);

  const handleClose = () => {
    methods.reset(defaultValues);
    setTabIndex(0);
    onClose();
  };

  return (
    <BaseDrawer isOpen={isOpen} onClose={handleClose}>
      <DrawerHeader>
        <Heading variant="h4">{copy}</Heading>
      </DrawerHeader>
      <DrawerBody p={0}>
        <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="slate" isLazy>
          <TabList px={0}>
            <Tab>Provider</Tab>
            <Tab>API Integration</Tab>
            <Tab>Identifiers</Tab>
          </TabList>
          <SimpleForm {...methods} submitOnBlur={false}>
            <TabPanels>
              <TabPanel>
                <ProviderForm />
              </TabPanel>
              <TabPanel>
                <ProviderApiForm />
              </TabPanel>
              <TabPanel>
                <ProviderIdentityForm />
              </TabPanel>
            </TabPanels>
          </SimpleForm>
        </Tabs>
      </DrawerBody>
      <DrawerFooter>
        <Stack direction="row" justify="space-between" align="center" w="full" spacing="2">
          <Show if={!!providerId}>
            <Text>ID: {providerId}</Text>
          </Show>
          <Button type="submit" colorScheme="brand" isLoading={isLoading} onClick={handleSubmit}>
            {copy}
          </Button>
        </Stack>
      </DrawerFooter>
    </BaseDrawer>
  );
};
